/* ============================================================================
   qm-pdp-v2.js — Quirky Moose · produktsiden, version 2 (2026-09-24)
   Loaded from snippets/qm-pdp-assets.liquid.
     1) Keeps «Your Grounded», «Many hang two», the dimensions drawing and the
        «How it works» drawings in step with the chosen length and colour.
     2) «Add 2»: submits the theme's own product form with quantity 2, so the
        cart drawer opens exactly as with the normal buy button.
     3) Picking a colour or length never moves the page: not in the product form, not in the
        sticky bar's sheet and not in the «how many pairs?» ladder (a tap on a row picks that length).
     4) Gallery images open in a framed lightbox over the page.
   ROLLBACK: remove the script line in snippets/qm-pdp-assets.liquid.
   ========================================================================= */
(() => {
  const picker = () => document.querySelector('variant-selects');

  const checkedColour = () => {
    const input = document.querySelector('variant-selects .product-form__input--swatch input:checked');
    if (!input) return null;
    const sw = input.nextElementSibling && input.nextElementSibling.querySelector('.swatch');
    if (!sw) return null;
    const bg = sw.style.getPropertyValue('--swatch--background');
    return bg && bg.indexOf('rgb') === 0 ? bg.trim() : null;
  };

  const checkedSize = () => {
    const input = document.querySelector('variant-selects .product-form__input--pill input:checked');
    return input ? input.value : null;
  };

  const sync = (variant) => {
    const colour = checkedColour();
    if (colour) document.querySelectorAll('[data-qm-colour-scope]').forEach((el) => el.style.setProperty('--qm-val', colour));

    const size = checkedSize();
    if (size) {
      document.querySelectorAll('[data-qm-mine]').forEach((card) => {
        card.querySelectorAll('[data-w]').forEach((el) => { el.hidden = el.dataset.w !== size; });
        const w = card.querySelector('[data-qm-w]');
        if (w) w.textContent = size;
        const noteEl = card.querySelector('[data-qm-note]');
        if (noteEl) {
          try {
            const notes = JSON.parse(card.dataset.notes || '{}');
            if (notes[size]) noteEl.textContent = notes[size];
          } catch (e) { /* keep the note that is there */ }
        }
      });
      document.querySelectorAll('[data-qm-tech]').forEach((el) => { el.hidden = el.dataset.qmTech !== size; });
      document.querySelectorAll('[data-qm-pick]').forEach((el) => el.setAttribute('aria-pressed', el.dataset.qmPick === size ? 'true' : 'false'));
    }

    if (variant && variant.id) {
      document.querySelectorAll('[data-qm-two]').forEach((box) => {
        try {
          const prices = JSON.parse(box.dataset.prices || '{}');
          const el = box.querySelector('[data-qm-two-price]');
          if (el && prices[variant.id]) el.textContent = prices[variant.id];
        } catch (e) { /* keep the price that is there */ }
      });
    }
  };

  const addTwo = (btn) => {
    const form = document.querySelector('product-form form[data-type="add-to-cart-form"]');
    if (!form) return;
    let qty = form.querySelector('input[name="quantity"][data-qm-two-qty]');
    if (!qty) {
      qty = document.createElement('input');
      qty.type = 'hidden';
      qty.name = 'quantity';
      qty.setAttribute('data-qm-two-qty', '');
      form.appendChild(qty);
    }
    qty.value = '2';
    btn.setAttribute('aria-busy', 'true');
    const submit = form.querySelector('[type="submit"]');
    if (typeof form.requestSubmit === 'function') form.requestSubmit(submit || undefined);
    else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    // The theme reads the form synchronously when the submit starts; take the extra field out again right after.
    setTimeout(() => { qty.remove(); btn.removeAttribute('aria-busy'); }, 0);
  };

  // After every variant change the theme moves the page twice: product-info.js focuses the chosen button
  // (the browser scrolls it into view) and media-gallery.js scrolls up to the new variant image. Wherever
  // the customer picks a colour or length, that throws the page back to the top (and on a phone the sticky
  // bar pops up because the buy button has left the screen). So while a pick loads, those two jumps are
  // switched off: focus still moves (screen readers), but without scrolling, and the page-scroll call is
  // skipped. The picture in the gallery still changes. Everything is back to normal as soon as the customer
  // scrolls, and at the latest a second after the change has landed.
  const pageScrollTo = window.scrollTo;
  const elementFocus = HTMLElement.prototype.focus;
  let hold = null;
  const releaseHold = () => {
    if (!hold) return;
    clearTimeout(hold.timer);
    window.scrollTo = pageScrollTo;
    HTMLElement.prototype.focus = elementFocus;
    hold = null;
    window.removeEventListener('scroll', keepPlace);
    ['wheel', 'touchstart', 'keydown'].forEach((t) => window.removeEventListener(t, releaseHold));
  };
  const keepPlace = () => {
    if (hold && Math.abs(window.scrollY - hold.y) > 1) pageScrollTo.call(window, 0, hold.y);
  };
  const holdScroll = () => {
    releaseHold();
    hold = { y: window.scrollY, timer: setTimeout(releaseHold, 5000) };
    window.scrollTo = function () { /* held — see above */ };
    HTMLElement.prototype.focus = function (options) {
      return elementFocus.call(this, Object.assign({}, options, { preventScroll: true }));
    };
    window.addEventListener('scroll', keepPlace, { passive: true });
    ['wheel', 'touchstart', 'keydown'].forEach((t) => window.addEventListener(t, releaseHold, { passive: true }));
  };

  const pickSize = (value) => {
    const input = Array.from(document.querySelectorAll('variant-selects .product-form__input--pill input')).find((i) => i.value === value);
    if (!input || input.checked) return;
    // The page is held by the change listener below.
    // checked + change rather than a click: a click also focuses the radio, and the browser scrolls a focused element into view.
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  // Dawn quirk: with the «stacked» desktop gallery and thumbnails switched on for mobile, the gallery slider
  // announces a slide change without a slide after a variant change, and media-gallery.js throws on it.
  // Such an event carries nothing to act on, so it is stopped before it reaches the gallery.
  window.addEventListener('slideChanged', (e) => {
    if (!e.detail || !e.detail.currentElement) e.stopImmediatePropagation();
  }, true);

  // ── Lightbox for the gallery images ──────────────────────────────────────
  // Dawn opens a full-screen page of stacked images. Instead the gallery's images open in a frame over the page,
  // a little smaller than the screen, with arrows, swipe, a counter and Esc/backdrop to close.
  // Videos and 3D models still open in Dawn's own viewer.
  const ICON = {
    close: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>',
    prev: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    next: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>'
  };
  // Button labels come from the theme's own translated buttons, so they follow the shop language.
  const labelOf = (selector, fallback) => {
    const el = document.querySelector(selector);
    return (el && el.getAttribute('aria-label')) || fallback;
  };
  const galleryImages = () => Array.from(document.querySelectorAll('media-gallery .product__media-list > li[data-media-id]'))
    .map((li) => {
      const img = li.querySelector('.product__modal-opener--image img');
      const toggle = li.querySelector('.product__media-toggle');
      if (!img || !toggle) return null;
      return {
        id: toggle.getAttribute('data-media-id'), src: img.getAttribute('src'), srcset: img.getAttribute('srcset'),
        alt: img.getAttribute('alt') || '', width: img.getAttribute('width'), height: img.getAttribute('height')
      };
    })
    .filter(Boolean);

  let lb = null;
  const lightbox = () => {
    if (lb) return lb;
    const root = document.createElement('div');
    root.className = 'qm-lb';
    root.hidden = true;
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.innerHTML =
      '<div class="qm-lb__frame">' +
        '<div class="qm-lb__track"></div>' +
        '<button type="button" class="qm-lb__btn qm-lb__close" data-lb="close">' + ICON.close + '</button>' +
        '<button type="button" class="qm-lb__btn qm-lb__prev" data-lb="prev">' + ICON.prev + '</button>' +
        '<button type="button" class="qm-lb__btn qm-lb__next" data-lb="next">' + ICON.next + '</button>' +
        '<div class="qm-lb__count" aria-live="polite"></div>' +
      '</div>';
    document.body.appendChild(root);
    const track = root.querySelector('.qm-lb__track');
    const count = root.querySelector('.qm-lb__count');
    const prev = root.querySelector('[data-lb="prev"]');
    const next = root.querySelector('[data-lb="next"]');
    const close = root.querySelector('[data-lb="close"]');
    close.setAttribute('aria-label', labelOf('product-modal .product-media-modal__toggle', 'Close'));
    prev.setAttribute('aria-label', labelOf('media-gallery .slider-button--prev', 'Previous'));
    next.setAttribute('aria-label', labelOf('media-gallery .slider-button--next', 'Next'));

    const state = { total: 0, opener: null, target: null };
    const current = () => Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
    const show = (i) => {
      count.textContent = (i + 1) + ' / ' + state.total;
      prev.disabled = i <= 0;
      next.disabled = i >= state.total - 1;
    };
    // While an arrow press is still gliding, keep showing where it is heading.
    const update = () => {
      if (state.target !== null && Math.abs(track.scrollLeft - state.target * track.clientWidth) < 2) state.target = null;
      show(state.target !== null ? state.target : current());
    };
    const smooth = () => !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const go = (step) => {
      const from = state.target !== null ? state.target : current();
      const to = Math.max(0, Math.min(state.total - 1, from + step));
      if (to === from) return;
      state.target = to;
      show(to);
      track.scrollTo({ left: to * track.clientWidth, behavior: smooth() ? 'smooth' : 'auto' });
    };
    // A swipe or wheel takes over from an arrow press.
    ['touchstart', 'wheel'].forEach((t) => track.addEventListener(t, () => { state.target = null; }, { passive: true }));
    const hide = () => {
      if (root.hidden) return;
      root.classList.remove('is-open');
      root.hidden = true;
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', onKey);
      if (state.opener) state.opener.focus({ preventScroll: true });
    };
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); hide(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
      else if (e.key === 'Tab') {
        // Keep keyboard focus inside the frame while it is open.
        const f = Array.from(root.querySelectorAll('button:not([disabled])'));
        if (!f.length) return;
        const i = f.indexOf(document.activeElement);
        e.preventDefault();
        f[(i + (e.shiftKey ? f.length - 1 : 1)) % f.length].focus();
      }
    };
    track.addEventListener('scroll', update, { passive: true });
    prev.addEventListener('click', () => go(-1));
    next.addEventListener('click', () => go(1));
    close.addEventListener('click', hide);
    // A click on the dimmed backdrop or on the empty space around the image closes it.
    root.addEventListener('click', (e) => {
      if (e.target === root || e.target.classList.contains('qm-lb__slide')) hide();
    });

    const open = (items, index, opener) => {
      state.total = items.length;
      state.opener = opener;
      state.target = null;
      track.innerHTML = '';
      items.forEach((it, i) => {
        const slide = document.createElement('div');
        slide.className = 'qm-lb__slide';
        const img = document.createElement('img');
        if (it.srcset) img.setAttribute('srcset', it.srcset);
        img.setAttribute('sizes', '(min-width: 750px) 90vw, 94vw');
        img.setAttribute('src', it.src);
        img.setAttribute('alt', it.alt);
        // Width and height give the picture its shape before it has loaded, so the frame does not jump.
        if (it.width && it.height) { img.setAttribute('width', it.width); img.setAttribute('height', it.height); }
        if (i !== index) img.setAttribute('loading', 'lazy');
        slide.appendChild(img);
        track.appendChild(slide);
      });
      root.hidden = false;
      document.body.classList.add('overflow-hidden');
      track.scrollLeft = index * track.clientWidth;
      update();
      document.addEventListener('keydown', onKey);
      close.focus({ preventScroll: true });
      setTimeout(() => root.classList.add('is-open'), 0);
    };
    lb = { open };
    return lb;
  };

  // Capture phase: runs before Dawn's modal-opener, which is then never told about the click.
  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('media-gallery .product__modal-opener--image .product__media-toggle');
    if (!toggle) return;
    const items = galleryImages();
    const index = items.findIndex((it) => it.id === toggle.getAttribute('data-media-id'));
    if (index < 0) return;
    e.preventDefault();
    e.stopPropagation();
    lightbox().open(items, index, toggle);
  }, true);

  // Capture phase: the hold is in place before the theme's variant-selects starts loading the new variant.
  // Catches the product form, the sticky bar's sheet and the ladder, as all three change the real inputs.
  document.addEventListener('change', (e) => {
    if (e.target instanceof HTMLInputElement && e.target.closest('variant-selects')) holdScroll();
  }, true);

  document.addEventListener('click', (e) => {
    const two = e.target.closest('[data-qm-two-add]');
    if (two) { e.preventDefault(); addTwo(two); return; }
    const row = e.target.closest('[data-qm-pick]');
    if (row) { e.preventDefault(); pickSize(row.dataset.qmPick); }
  });

  const hook = () => {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return false;
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const data = event && event.data ? event.data : null;
      if (hold) { clearTimeout(hold.timer); hold.timer = setTimeout(releaseHold, 1000); }
      // The theme has already swapped the picker HTML when it publishes; read the new state right after.
      // (setTimeout rather than requestAnimationFrame: rAF never fires in a background tab.)
      setTimeout(() => sync(data && data.variant), 0);
    });
    return true;
  };

  const boot = () => {
    if (!hook()) document.addEventListener('DOMContentLoaded', hook, { once: true });
    if (picker()) sync(null);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
